import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FormattedMessage, useIntl } from "react-intl";
import moment from "moment";
import axios from "axios";
import { useHistory, useLocation } from "react-router-dom";
import { InlineLoading, Module, ModuleBody, Wrapper } from "@wfp/ui";

import {
  buildDashboardSummaryRequestKey,
  fetchDashboardSummary,
  fetchSchoolYears,
  getDashboardMatched,
  getDashboardSummary,
  getDashboardSummaryLastSuccessKey,
  getAnalyticsIsFetching,
  getAnalyticsSchoolYears,
  getAnalyticsSchoolYearsIsFetching,
  DashboardSummaryEntry,
} from "data-handler/ducks/analytics";
import {
  requestSchoolAdmins,
  getSchoolAdmins,
  getAdminLevelsIsFetching,
} from "data-handler/ducks/adminLevels";
import {
  getAllAdminSchools,
  getIsSchoolsFetching,
  requestAllAdminSchools,
} from "data-handler/ducks/schools";
import { getSelectedCountry } from "data-handler/ducks/countryProfile";
import Filtering from "components/CountryAdmin/CountryAdminContent/Table/Filtering";
import SummaryCard from "./SummaryCard";
import MiniBarChart from "./MiniBarChart";
import MiniLineChart from "./MiniLineChart";
import CommodityPriceVarianceChart from "./CommodityPriceVarianceChart";
import DashboardGuide from "./DashboardGuide";

import "./_dashboard.scss";

type DashboardFilters = {
  adm_1?: string;
  adm_2?: string;
  adm_3?: string;
  adm_4?: string;
  school_id?: string;
  school_year?: string;
  ongoing_school_year?: boolean;
  year_month?: string;
};

const ADMIN_FILTER_KEYS: (keyof DashboardFilters)[] = [
  "adm_1",
  "adm_2",
  "adm_3",
  "adm_4",
];

const MULTI_FILTER_KEYS: (keyof DashboardFilters)[] = [
  ...ADMIN_FILTER_KEYS,
  "school_id",
  "school_year",
  "year_month",
];

const parseFiltersFromSearch = (search: string): DashboardFilters => {
  const params = new URLSearchParams(search);
  const adm1 = params.getAll("adm_1").filter(Boolean).join(",");
  const adm2 = params.getAll("adm_2").filter(Boolean).join(",");
  const adm3 = params.getAll("adm_3").filter(Boolean).join(",");
  const adm4 = params.getAll("adm_4").filter(Boolean).join(",");
  const schoolId = params.getAll("school_id").filter(Boolean).join(",");
  const schoolYearValues = params.getAll("school_year").filter(Boolean);
  const schoolYear = schoolYearValues.length
    ? schoolYearValues.join(",")
    : params.get("year") || "";
  const yearMonthValues = params.getAll("year_month").filter(Boolean);
  const yearMonth = yearMonthValues.length
    ? yearMonthValues.join(",")
    : params.get("month") || "";
  const ongoingSchoolYear = params.get("ongoing_school_year") === "true";

  return {
    ...(adm1 ? { adm_1: adm1 } : {}),
    ...(adm2 ? { adm_2: adm2 } : {}),
    ...(adm3 ? { adm_3: adm3 } : {}),
    ...(adm4 ? { adm_4: adm4 } : {}),
    ...(schoolId ? { school_id: schoolId } : {}),
    ...(schoolYear ? { school_year: schoolYear } : {}),
    ...(yearMonth ? { year_month: yearMonth } : {}),
    ...(ongoingSchoolYear ? { ongoing_school_year: true } : {}),
  };
};

const getEntryYearMonth = (entry: DashboardSummaryEntry): string =>
  entry.year_month || entry.month || "";

const getUniqueYearMonths = (entries: DashboardSummaryEntry[]): string[] =>
  Array.from(
    new Set(entries.map((entry) => getEntryYearMonth(entry)).filter(Boolean))
  ).sort((a, b) => b.localeCompare(a));

const parseDashboardEntries = (rawData: any): DashboardSummaryEntry[] => {
  if (Array.isArray(rawData)) {
    return rawData;
  }

  if (Array.isArray(rawData?.results)) {
    return rawData.results;
  }

  if (Array.isArray(rawData?.data)) {
    return rawData.data;
  }

  return [];
};

const formatDateRange = (data: DashboardSummaryEntry[]) => {
  if (!data.length) return "";
  const first = moment(getEntryYearMonth(data[0]), "YYYY-MM").format(
    "MMM YYYY"
  );
  const last = moment(
    getEntryYearMonth(data[data.length - 1]),
    "YYYY-MM"
  ).format("MMM YYYY");
  return `${first} - ${last}`;
};

const splitFilterValues = (value?: string): string[] =>
  String(value || "")
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);

const getSingleFilterValue = (value?: string): string | undefined => {
  const values = splitFilterValues(value);
  return values.length === 1 ? values[0] : undefined;
};

const toSelectedAdmins = (filters: DashboardFilters, level: number) => {
  if (level <= 1) {
    return {};
  }

  const selectedAdmins: DashboardFilters = {};

  if (level >= 2 && filters.adm_1) selectedAdmins.adm_1 = filters.adm_1;
  if (level >= 3 && filters.adm_2) selectedAdmins.adm_2 = filters.adm_2;
  if (level >= 4 && filters.adm_3) selectedAdmins.adm_3 = filters.adm_3;

  return selectedAdmins;
};

const Dashboard: React.FC = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const intl = useIntl();
  const selectedCountry = useSelector(getSelectedCountry);
  const summary = useSelector(getDashboardSummary);
  const matchedSchools = useSelector(getDashboardMatched);
  const dashboardSummaryLastSuccessKey = useSelector(
    getDashboardSummaryLastSuccessKey
  );
  const isFetching = useSelector(getAnalyticsIsFetching);
  const schoolYears = useSelector(getAnalyticsSchoolYears);
  const schoolYearsFetching = useSelector(getAnalyticsSchoolYearsIsFetching);
  const schoolAdmins = useSelector(getSchoolAdmins);
  const adminLevelsFetching = useSelector(getAdminLevelsIsFetching);
  const allAdminSchools = useSelector(getAllAdminSchools);
  const schoolsFetching = useSelector(getIsSchoolsFetching);
  const [filtersState, setFiltersState] = useState<DashboardFilters>(() =>
    parseFiltersFromSearch(location.search)
  );
  const [liveFiltersState, setLiveFiltersState] = useState<DashboardFilters>(
    () => parseFiltersFromSearch(location.search)
  );
  const [availableYearMonths, setAvailableYearMonths] = useState<string[]>([]);

  const selectedCountryId = useMemo(
    () => (selectedCountry?.id ? String(selectedCountry.id) : ""),
    [selectedCountry]
  );
  const purchasePriceCurrencyCode = selectedCountry?.currency?.code || "";

  const adm1Options = useMemo(
    () =>
      (schoolAdmins?.[1] || []).map((a) => ({
        name: String(a.id),
        label: a.name,
      })),
    [schoolAdmins]
  );

  const yearMonthOptions = useMemo(() => {
    const selectedYearMonths = splitFilterValues(liveFiltersState.year_month);
    const options = Array.from(
      new Set([...availableYearMonths, ...selectedYearMonths])
    ).sort((a, b) => b.localeCompare(a));

    return options.map((value) => ({
      name: value,
      label: value,
    }));
  }, [availableYearMonths, liveFiltersState.year_month]);

  const schoolYearOptions = useMemo(
    () =>
      (schoolYears || []).map((value) => ({
        name: String(value),
        label: String(value),
      })),
    [schoolYears]
  );

  const schoolOptions = useMemo(
    () =>
      (allAdminSchools?.ids || [])
        .map((id: number) => allAdminSchools.results?.[id])
        .filter(Boolean)
        .map((school: any) => ({
          name: String(school.id),
          label: school.name,
        })),
    [allAdminSchools]
  );

  const adm2Options = useMemo(
    () =>
      (schoolAdmins?.[2] || []).map((a) => ({
        name: String(a.id),
        label: a.name,
      })),
    [schoolAdmins]
  );

  const adm3Options = useMemo(
    () =>
      (schoolAdmins?.[3] || []).map((a) => ({
        name: String(a.id),
        label: a.name,
      })),
    [schoolAdmins]
  );

  const adm4Options = useMemo(
    () =>
      (schoolAdmins?.[4] || []).map((a) => ({
        name: String(a.id),
        label: a.name,
      })),
    [schoolAdmins]
  );

  const adm1SingleValue = useMemo(
    () => getSingleFilterValue(filtersState.adm_1),
    [filtersState.adm_1]
  );

  const adm2SingleValue = useMemo(
    () => getSingleFilterValue(filtersState.adm_2),
    [filtersState.adm_2]
  );

  const adm3SelectedId = useMemo(
    () => getSingleFilterValue(filtersState.adm_3),
    [filtersState.adm_3]
  );

  const filterFields = useMemo(
    () => [
      {
        name: "adm_1",
        label: "Admin 1",
        type: "multiselect",
        disabled: adminLevelsFetching || !selectedCountryId,
        options: adm1Options,
      },
      {
        name: "adm_2",
        label: "Admin 2",
        type: "multiselect",
        disabled: adminLevelsFetching || !selectedCountryId,
        options: adm2Options,
      },
      {
        name: "adm_3",
        label: "Admin 3",
        type: "multiselect",
        disabled: adminLevelsFetching || !selectedCountryId,
        options: adm3Options,
      },
      {
        name: "adm_4",
        label: "Admin 4",
        type: "multiselect",
        disabled: adminLevelsFetching || !selectedCountryId,
        options: adm4Options,
      },
      {
        name: "school_id",
        label: intl.formatMessage({
          id: "countryAdmin.school",
          defaultMessage: "School",
        }),
        type: "multiselect",
        disabled: schoolsFetching || !selectedCountryId,
        options: schoolOptions,
        className: "dashboard__filter-school",
        newRow: true,
      },
      {
        name: "school_year",
        label: intl.formatMessage({
          id: "CountryAdmin.schoolYear",
          defaultMessage: "School year",
        }),
        type: "multiselect",
        disabled: schoolYearsFetching || !selectedCountryId,
        options: schoolYearOptions,
      },
      {
        name: "year_month",
        label: intl.formatMessage({
          id: "CountryAdmin.month",
          defaultMessage: "Month",
        }),
        type: "multiselect",
        options: yearMonthOptions,
      },
      {
        name: "ongoing_school_year",
        label: intl.formatMessage({
          id: "analytics.dashboard.ongoingSchoolYear",
          defaultMessage: "Ongoing school year",
        }),
        type: "checkbox",
      },
    ],
    [
      adm1Options,
      adm2Options,
      adm3Options,
      adm4Options,
      adminLevelsFetching,
      intl,
      schoolOptions,
      schoolsFetching,
      schoolYearsFetching,
      selectedCountryId,
      schoolYearOptions,
      yearMonthOptions,
    ]
  );

  const filterDefaults = useMemo(() => filtersState, [filtersState]);

  const isSingleSchoolSelected = useMemo(
    () => splitFilterValues(filtersState.school_id).length === 1,
    [filtersState.school_id]
  );

  const currentDashboardRequestKey = useMemo(() => {
    if (!selectedCountryId) {
      return "";
    }

    return buildDashboardSummaryRequestKey(
      Number(selectedCountryId),
      filtersState.school_id,
      filtersState.school_year,
      filtersState.ongoing_school_year,
      filtersState.year_month,
      filtersState.adm_1,
      filtersState.adm_2,
      filtersState.adm_3,
      filtersState.adm_4
    );
  }, [
    filtersState.adm_1,
    filtersState.adm_2,
    filtersState.adm_3,
    filtersState.adm_4,
    filtersState.year_month,
    filtersState.school_id,
    filtersState.school_year,
    filtersState.ongoing_school_year,
    selectedCountryId,
  ]);

  const yearMonthScopeKey = useMemo(
    () =>
      [
        selectedCountryId,
        liveFiltersState.school_id || "",
        liveFiltersState.school_year || "",
        liveFiltersState.ongoing_school_year ? "1" : "0",
        liveFiltersState.adm_1 || "",
        liveFiltersState.adm_2 || "",
        liveFiltersState.adm_3 || "",
        liveFiltersState.adm_4 || "",
      ].join("|"),
    [
      liveFiltersState.adm_1,
      liveFiltersState.adm_2,
      liveFiltersState.adm_3,
      liveFiltersState.adm_4,
      liveFiltersState.school_id,
      liveFiltersState.school_year,
      liveFiltersState.ongoing_school_year,
      selectedCountryId,
    ]
  );

  useEffect(() => {
    const nextFilters = parseFiltersFromSearch(location.search);
    setFiltersState((prev) => {
      const prevSchoolId = prev.school_id || "";
      const prevSchoolYear = prev.school_year || "";
      const prevOngoingSchoolYear = Boolean(prev.ongoing_school_year);
      const prevYearMonth = prev.year_month || "";
      const prevAdm1 = prev.adm_1 || "";
      const prevAdm2 = prev.adm_2 || "";
      const prevAdm3 = prev.adm_3 || "";
      const prevAdm4 = prev.adm_4 || "";
      const nextSchoolId = nextFilters.school_id || "";
      const nextSchoolYear = nextFilters.school_year || "";
      const nextOngoingSchoolYear = Boolean(nextFilters.ongoing_school_year);
      const nextYearMonth = nextFilters.year_month || "";
      const nextAdm1 = nextFilters.adm_1 || "";
      const nextAdm2 = nextFilters.adm_2 || "";
      const nextAdm3 = nextFilters.adm_3 || "";
      const nextAdm4 = nextFilters.adm_4 || "";

      if (
        prevSchoolId === nextSchoolId &&
        prevSchoolYear === nextSchoolYear &&
        prevOngoingSchoolYear === nextOngoingSchoolYear &&
        prevYearMonth === nextYearMonth &&
        prevAdm1 === nextAdm1 &&
        prevAdm2 === nextAdm2 &&
        prevAdm3 === nextAdm3 &&
        prevAdm4 === nextAdm4
      ) {
        return prev;
      }

      return nextFilters;
    });
    setLiveFiltersState(nextFilters);
  }, [location.search]);

  useEffect(() => {
    if (selectedCountryId) {
      dispatch(fetchSchoolYears(Number(selectedCountryId)));
      dispatch(requestSchoolAdmins(1, selectedCountryId));
    }
  }, [dispatch, selectedCountryId]);

  useEffect(() => {
    if (!selectedCountryId) {
      setAvailableYearMonths([]);
      return;
    }

    const params = new URLSearchParams({ country_id: selectedCountryId });
    const appendRepeated = (key: string, value?: string) => {
      splitFilterValues(value).forEach((item) => params.append(key, item));
    };

    appendRepeated("school_id", liveFiltersState.school_id);
    appendRepeated("school_year", liveFiltersState.school_year);
    appendRepeated("adm_1", liveFiltersState.adm_1);
    appendRepeated("adm_2", liveFiltersState.adm_2);
    appendRepeated("adm_3", liveFiltersState.adm_3);
    appendRepeated("adm_4", liveFiltersState.adm_4);

    if (liveFiltersState.ongoing_school_year) {
      params.set("ongoing_school_year", "true");
    }

    let active = true;

    axios({
      method: "GET",
      url: `${
        process.env.REACT_APP_API_URL
      }/analytics/dashboard_summary/?${params.toString()}`,
    })
      .then((response) => {
        if (!active) {
          return;
        }

        const entries = parseDashboardEntries(response.data);
        setAvailableYearMonths(getUniqueYearMonths(entries));
      })
      .catch(() => {
        if (!active) {
          return;
        }

        setAvailableYearMonths([]);
      });

    return () => {
      active = false;
    };
  }, [
    liveFiltersState.adm_1,
    liveFiltersState.adm_2,
    liveFiltersState.adm_3,
    liveFiltersState.adm_4,
    liveFiltersState.school_id,
    liveFiltersState.school_year,
    liveFiltersState.ongoing_school_year,
    selectedCountryId,
    yearMonthScopeKey,
  ]);

  useEffect(() => {
    if (!availableYearMonths.length && summary.length) {
      setAvailableYearMonths(getUniqueYearMonths(summary));
    }
  }, [availableYearMonths.length, summary]);

  const handleFieldChange = useCallback(
    (fieldName, value, nextState) => {
      setLiveFiltersState(nextState || {});

      if (!selectedCountryId) return;
      const nextAdm1SingleValue = getSingleFilterValue(nextState?.adm_1);
      const nextAdm2SingleValue = getSingleFilterValue(nextState?.adm_2);

      if (fieldName === "adm_1") {
        const nextFilters = nextState || {};
        const selectedAdminsForLevel2 = toSelectedAdmins(nextFilters, 2);
        const selectedAdminsForLevel3 = toSelectedAdmins(nextFilters, 3);
        const selectedAdminsForLevel4 = toSelectedAdmins(nextFilters, 4);
        dispatch(
          requestSchoolAdmins(
            2,
            selectedCountryId,
            nextAdm1SingleValue,
            undefined,
            selectedAdminsForLevel2
          )
        );
        dispatch(
          requestSchoolAdmins(
            3,
            selectedCountryId,
            undefined,
            nextAdm1SingleValue,
            selectedAdminsForLevel3
          )
        );
        dispatch(
          requestSchoolAdmins(
            4,
            selectedCountryId,
            undefined,
            nextAdm1SingleValue,
            selectedAdminsForLevel4
          )
        );
      }
      if (fieldName === "adm_2" && !nextState?.adm_1 && value) {
        dispatch(
          requestSchoolAdmins(
            2,
            selectedCountryId,
            undefined,
            undefined,
            toSelectedAdmins(nextState || {}, 2)
          )
        );
      }
      if (fieldName === "adm_2") {
        dispatch(
          requestSchoolAdmins(
            3,
            selectedCountryId,
            nextAdm2SingleValue,
            nextAdm1SingleValue,
            toSelectedAdmins(nextState || {}, 3)
          )
        );
      }
      if (fieldName === "adm_3") {
        const nextAdm3SingleValue = getSingleFilterValue(nextState?.adm_3);

        dispatch(
          requestSchoolAdmins(
            4,
            selectedCountryId,
            nextAdm3SingleValue,
            nextAdm1SingleValue,
            toSelectedAdmins(nextState || {}, 4)
          )
        );
      }
    },
    [dispatch, selectedCountryId]
  );

  useEffect(() => {
    if (selectedCountryId) {
      dispatch(
        requestAllAdminSchools(
          99999,
          1,
          "",
          [{ id: "country", value: selectedCountryId }],
          true,
          "list"
        )
      );
    }
  }, [dispatch, selectedCountryId]);

  useEffect(() => {
    if (selectedCountryId) {
      dispatch(
        requestSchoolAdmins(
          2,
          selectedCountryId,
          adm1SingleValue,
          undefined,
          toSelectedAdmins(filtersState, 2)
        )
      );
    }
  }, [adm1SingleValue, dispatch, filtersState, selectedCountryId]);

  useEffect(() => {
    if (selectedCountryId) {
      dispatch(
        requestSchoolAdmins(
          3,
          selectedCountryId,
          adm2SingleValue,
          adm1SingleValue,
          toSelectedAdmins(filtersState, 3)
        )
      );
    }
  }, [
    adm1SingleValue,
    adm2SingleValue,
    dispatch,
    filtersState,
    selectedCountryId,
  ]);

  useEffect(() => {
    if (selectedCountryId) {
      dispatch(
        requestSchoolAdmins(
          4,
          selectedCountryId,
          adm3SelectedId,
          adm1SingleValue,
          toSelectedAdmins(filtersState, 4)
        )
      );
    }
  }, [
    adm1SingleValue,
    adm3SelectedId,
    dispatch,
    filtersState,
    selectedCountryId,
  ]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    let shouldReplace = false;
    const filterKeys: (keyof DashboardFilters)[] = [
      "adm_1",
      "adm_2",
      "adm_3",
      "adm_4",
      "school_id",
      "school_year",
      "ongoing_school_year",
      "year_month",
    ];

    const currentCountryId = params.get("country_id") || "";
    const isCountryChanged =
      Boolean(selectedCountryId) &&
      Boolean(currentCountryId) &&
      currentCountryId !== selectedCountryId;

    if (isCountryChanged) {
      return;
    }

    if (selectedCountryId) {
      if (currentCountryId !== selectedCountryId) {
        params.set("country_id", selectedCountryId);
        shouldReplace = true;
      }
    }

    if (params.has("month")) {
      params.delete("month");
      shouldReplace = true;
    }

    if (params.has("year")) {
      params.delete("year");
      shouldReplace = true;
    }

    filterKeys.forEach((key) => {
      const value = filtersState[key];

      if (MULTI_FILTER_KEYS.includes(key)) {
        const nextValues = splitFilterValues(value);
        const currentValues = params.getAll(key).filter(Boolean);

        if (nextValues.join(",") !== currentValues.join(",")) {
          params.delete(key);
          nextValues.forEach((item) => params.append(key, item));
          shouldReplace = true;
        }

        return;
      }

      if (key === "ongoing_school_year") {
        const nextValue = Boolean(value);
        const currentValue = params.get(key) === "true";

        if (nextValue !== currentValue) {
          if (nextValue) {
            params.set(key, "true");
          } else {
            params.delete(key);
          }
          shouldReplace = true;
        }

        return;
      }

      if (value) {
        if (params.get(key) !== value) {
          params.set(key, value);
          shouldReplace = true;
        }
      } else if (params.has(key)) {
        params.delete(key);
        shouldReplace = true;
      }
    });

    if (shouldReplace) {
      const nextSearch = params.toString();
      history.replace({
        pathname: location.pathname,
        search: nextSearch ? `?${nextSearch}` : "",
      });
    }
  }, [
    filtersState,
    history,
    location.pathname,
    location.search,
    selectedCountryId,
  ]);

  useEffect(() => {
    if (selectedCountryId) {
      const params = new URLSearchParams(location.search);
      if (params.get("country_id") !== selectedCountryId) {
        return;
      }

      if (
        currentDashboardRequestKey &&
        currentDashboardRequestKey === dashboardSummaryLastSuccessKey
      ) {
        return;
      }

      dispatch(
        fetchDashboardSummary(
          Number(selectedCountryId),
          filtersState.school_id,
          filtersState.school_year,
          filtersState.ongoing_school_year,
          filtersState.year_month,
          filtersState.adm_1,
          filtersState.adm_2,
          filtersState.adm_3,
          filtersState.adm_4
        )
      );
    }
  }, [
    currentDashboardRequestKey,
    dashboardSummaryLastSuccessKey,
    dispatch,
    filtersState.adm_1,
    filtersState.adm_2,
    filtersState.adm_3,
    filtersState.adm_4,
    filtersState.year_month,
    filtersState.school_id,
    filtersState.school_year,
    filtersState.ongoing_school_year,
    location.search,
    selectedCountryId,
  ]);

  const sorted = useMemo(
    () =>
      [...summary].sort((a, b) =>
        getEntryYearMonth(a).localeCompare(getEntryYearMonth(b))
      ),
    [summary]
  );

  const latest = sorted.length ? sorted[sorted.length - 1] : null;
  const dateRange = formatDateRange(sorted);
  const firstMonthLabel = sorted.length
    ? moment(getEntryYearMonth(sorted[0]), "YYYY-MM").format("MMM YYYY")
    : "";
  const lastMonthLabel = sorted.length
    ? moment(getEntryYearMonth(sorted[sorted.length - 1]), "YYYY-MM").format(
        "MMM YYYY"
      )
    : "";
  const latestMonthTag = sorted.length
    ? moment(getEntryYearMonth(sorted[sorted.length - 1]), "YYYY-MM")
        .format("MMM")
        .toUpperCase()
    : "";

  const allMonths = useMemo(
    () => sorted.map((e) => getEntryYearMonth(e)).filter(Boolean),
    [sorted]
  );

  const goToDataDownload = useCallback(
    (dataset: string) => {
      const params = new URLSearchParams(location.search);
      params.set("dataset", dataset);
      if (allMonths.length) {
        params.set("months", allMonths.join(","));
      }
      const currentPath = location.pathname;
      const basePath = currentPath.replace(/\/Dashboard\/?$/, "");
      history.push({
        pathname: `${basePath}/DataDownload`,
        search: `?${params.toString()}`,
      });
    },
    [allMonths, history, location.pathname, location.search]
  );

  const boysLabel = intl.formatMessage({
    id: "analytics.dashboard.boys",
    defaultMessage: "Boys",
  });
  const girlsLabel = intl.formatMessage({
    id: "analytics.dashboard.girls",
    defaultMessage: "Girls",
  });
  const totalLabel = intl.formatMessage({
    id: "analytics.dashboard.total",
    defaultMessage: "Total",
  });
  const kgConsumedLabel = intl.formatMessage({
    id: "analytics.dashboard.kgConsumedTotal",
    defaultMessage: "Total In-kind + purchased commodities consumed",
  });
  const kgConsumedWfpLabel = intl.formatMessage({
    id: "analytics.dashboard.kgConsumedInKind",
    defaultMessage: "Total in-kind commodities consumed",
  });
  const kgConsumedNonWfpLabel = intl.formatMessage({
    id: "analytics.dashboard.kgConsumedPurchased",
    defaultMessage: "Total purchased commodities consumed",
  });
  const kgDeliveredLabel = intl.formatMessage({
    id: "analytics.dashboard.FoodDeliveredKg",
    defaultMessage: "Food Delivered (Kg)",
  });
  const kgPurchasedLabel = intl.formatMessage({
    id: "analytics.dashboard.FoodPurchasedKg",
    defaultMessage: "Food Purchased (Kg)",
  });
  const mealsServedLabel = intl.formatMessage({
    id: "analytics.dashboard.mealsServed",
    defaultMessage: "Meals served",
  });
  const contributionsServedLabel = intl.formatMessage({
    id: "analytics.dashboard.contributionsServed",
    defaultMessage: "Contributions Kg served",
  });
  const totalMoneySpentLabel = intl.formatMessage({
    id: "analytics.dashboard.totalMoneySpent",
    defaultMessage: "Total Money Spent",
  });
  const foodCostLabel = intl.formatMessage({
    id: "analytics.dashboard.foodCost",
    defaultMessage: "Food cost",
  });
  const otherCostsLabel = intl.formatMessage({
    id: "analytics.dashboard.otherCosts",
    defaultMessage: "Other costs",
  });
  const pricePaidLabel = intl.formatMessage({
    id: "analytics.dashboard.pricePerKg",
    defaultMessage: "Price per Kg",
  });
  const averageCostLabel = intl.formatMessage({
    id: "analytics.dashboard.averagePrice",
    defaultMessage: "Average price",
  });
  const feedingDaysPerSchoolAverageLabel = intl.formatMessage({
    id: "analytics.dashboard.feedingDaysPerSchoolAverage",
    defaultMessage: "Per school average",
  });
  const activeSchoolsLabel = intl.formatMessage({
    id: "analytics.dashboard.activeSchools",
    defaultMessage: "Active schools",
  });
  const barChartData = useMemo(
    () =>
      sorted.map((e) => ({
        month: getEntryYearMonth(e),
        male: e.enrollees_latest.male,
        female: e.enrollees_latest.female,
      })),
    [sorted]
  );

  const newcomersBarData = useMemo(
    () =>
      sorted.map((e) => ({
        month: getEntryYearMonth(e),
        male: e.newcomers.male,
        female: e.newcomers.female,
      })),
    [sorted]
  );

  const attendeesBarData = useMemo(
    () =>
      sorted.map((e) => ({
        month: getEntryYearMonth(e),
        male: e.attendees_average.male,
        female: e.attendees_average.female,
      })),
    [sorted]
  );

  const studentAttendanceBarData = useMemo(
    () =>
      sorted.map((e) => ({
        month: getEntryYearMonth(e),
        male: e.student_attendance?.male || 0,
        female: e.student_attendance?.female || 0,
      })),
    [sorted]
  );

  const beneficiariesBarData = useMemo(
    () =>
      sorted.map((e) => ({
        month: getEntryYearMonth(e),
        male: e.beneficiaries.male,
        female: e.beneficiaries.female,
      })),
    [sorted]
  );

  const beneficiariesAverageBarData = useMemo(
    () =>
      sorted.map((e) => ({
        month: getEntryYearMonth(e),
        male: e.beneficiaries_average?.male || 0,
        female: e.beneficiaries_average?.female || 0,
      })),
    [sorted]
  );

  const approvedReportsBarData = useMemo(
    () =>
      sorted.map((e) => ({
        month: getEntryYearMonth(e),
        value: e.approved_reports_rate || 0,
      })),
    [sorted]
  );

  const activeSchoolsBarData = useMemo(
    () =>
      sorted.map((e) => ({
        month: getEntryYearMonth(e),
        value: e.active_schools || 0,
      })),
    [sorted]
  );

  const averageRationLineData = useMemo(
    () =>
      sorted.map((e) => ({
        month: getEntryYearMonth(e),
        value: e.average_ration_per_student || 0,
      })),
    [sorted]
  );

  const consumedKgBarData = useMemo(
    () =>
      sorted.map((e) => ({
        month: getEntryYearMonth(e),
        value: e.kg_consumed_total || 0,
      })),
    [sorted]
  );

  const consumedWfpKgBarData = useMemo(
    () =>
      sorted.map((e) => ({
        month: getEntryYearMonth(e),
        value: e.kg_consumed_total_wfp || 0,
      })),
    [sorted]
  );

  const consumedNonWfpKgBarData = useMemo(
    () =>
      sorted.map((e) => ({
        month: getEntryYearMonth(e),
        value: e.kg_consumed_total_non_wfp || 0,
      })),
    [sorted]
  );

  const mealsServedBarData = useMemo(
    () =>
      sorted.map((e) => ({
        month: getEntryYearMonth(e),
        value: e.meals_served || 0,
      })),
    [sorted]
  );

  const contributionsServedBarData = useMemo(
    () =>
      sorted.map((e) => ({
        month: getEntryYearMonth(e),
        value: e.contributions_served || 0,
      })),
    [sorted]
  );

  const deliveredKgBarData = useMemo(
    () =>
      sorted.map((e) => ({
        month: getEntryYearMonth(e),
        value: e.kg_delivered_total || 0,
      })),
    [sorted]
  );

  const purchasedKgBarData = useMemo(
    () =>
      sorted.map((e) => ({
        month: getEntryYearMonth(e),
        value: e.kg_purchased_total || 0,
      })),
    [sorted]
  );

  const expensesBarData = useMemo(
    () =>
      sorted.map((e) => ({
        month: getEntryYearMonth(e),
        food_cost: e.expenses?.food_cost || 0,
        other_cost: e.expenses?.other_cost || 0,
      })),
    [sorted]
  );

  const commodityPriceVarianceData = useMemo(() => {
    return sorted.flatMap((entry) => {
      const monthValue = getEntryYearMonth(entry);
      return Array.isArray(entry.commodity_price_variance)
        ? entry.commodity_price_variance.map((varianceEntry) => ({
            ...varianceEntry,
            year_month: varianceEntry.year_month || monthValue,
          }))
        : [];
    });
  }, [sorted]);

  const feedingDaysLineData = useMemo(
    () =>
      sorted.map((e) => ({
        month: getEntryYearMonth(e),
        value: e.feeding_days_rate,
        tooltipExtraValue: e.feeding_days_rate_per_school_average || 0,
      })),
    [sorted]
  );

  const filledSchoolDaysLineData = useMemo(
    () =>
      sorted.map((e) => ({
        month: getEntryYearMonth(e),
        value: e.filled_school_days_rate || 0,
      })),
    [sorted]
  );

  const canteenAttendanceRateLineData = useMemo(
    () =>
      sorted.map((e) => ({
        month: getEntryYearMonth(e),
        value: e.canteen_attendance_rate || 0,
      })),
    [sorted]
  );

  if (!selectedCountry) {
    return (
      <Wrapper pageWidth="lg" className="dashboard">
        <Module>
          <ModuleBody>
            <p className="dashboard__no-country">
              <FormattedMessage
                id="analytics.dashboard.selectCountry"
                defaultMessage="Please select a country to view the dashboard."
              />
            </p>
          </ModuleBody>
        </Module>
      </Wrapper>
    );
  }

  const handleFilterSubmit = (filters: DashboardFilters) => {
    setFiltersState(filters);
    setLiveFiltersState(filters);
  };

  return (
    <Wrapper pageWidth="lg" className="dashboard">
      <div className="dashboard__filters">
        <Filtering
          fields={filterFields}
          onFieldChange={handleFieldChange}
          buttonsNewRow
          controlledResets={{
            adm_1: ["adm_2", "adm_3", "adm_4"],
            adm_2: ["adm_3", "adm_4"],
            adm_3: ["adm_4"],
            school_year: ["year_month"],
          }}
          onSubmit={handleFilterSubmit}
          defaults={filterDefaults}
        />
        {!isFetching &&
          sorted.length > 0 &&
          typeof matchedSchools === "number" && (
            <p className="dashboard__matched-schools">
              <FormattedMessage
                id="analytics.dashboard.matchedSchools"
                defaultMessage="Displaying data for {matchedSchools} active schools from {firstMonthLabel} to {lastMonthLabel}"
                values={{
                  matchedSchools,
                  firstMonthLabel,
                  lastMonthLabel,
                }}
              />
            </p>
          )}
      </div>

      {isFetching ? (
        <Module>
          <ModuleBody>
            <InlineLoading description="Loading..." />
          </ModuleBody>
        </Module>
      ) : !latest ? (
        <Module>
          <ModuleBody>
            <p className="dashboard__no-data">
              <FormattedMessage
                id="analytics.dashboard.noData"
                defaultMessage="No data available."
              />
            </p>
          </ModuleBody>
        </Module>
      ) : (
        <div className="dashboard__sections">
          <section className="dashboard__section">
            <div className="dashboard__section-header">
              <h3 className="dashboard__section-title">
                <FormattedMessage
                  id="analytics.dashboard.section.enrolmentsAttendance"
                  defaultMessage="Enrolments and Attendance"
                />
              </h3>
              <DashboardGuide />
            </div>
            <div className="dashboard__grid">
              <SummaryCard
                label={
                  <FormattedMessage
                    id="analytics.dashboard.currentEnrolment"
                    defaultMessage="Current Enrolments"
                  />
                }
                value={latest.enrollees_latest.total}
                detail={dateRange}
                latestMonthTag={latestMonthTag}
                onDownloadData={() =>
                  goToDataDownload("enrolment_attendance_monthly")
                }
              >
                <MiniBarChart
                  data={barChartData}
                  boysLabel={boysLabel}
                  girlsLabel={girlsLabel}
                  totalLabel={totalLabel}
                />
              </SummaryCard>

              <SummaryCard
                label={
                  <FormattedMessage
                    id="analytics.dashboard.newEnrolments"
                    defaultMessage="New Enrolments"
                  />
                }
                value={latest.newcomers.total}
                detail={dateRange}
                latestMonthTag={latestMonthTag}
                onDownloadData={() =>
                  goToDataDownload("enrolment_attendance_monthly")
                }
              >
                <MiniBarChart
                  data={newcomersBarData}
                  boysLabel={boysLabel}
                  girlsLabel={girlsLabel}
                  totalLabel={totalLabel}
                />
              </SummaryCard>

              <SummaryCard
                label={
                  <FormattedMessage
                    id="analytics.dashboard.studentAttendance"
                    defaultMessage="Student Attendance"
                  />
                }
                value={latest.student_attendance?.total || 0}
                detail={dateRange}
                latestMonthTag={latestMonthTag}
                onDownloadData={() =>
                  goToDataDownload("enrolment_attendance_monthly")
                }
              >
                <MiniBarChart
                  data={studentAttendanceBarData}
                  boysLabel={boysLabel}
                  girlsLabel={girlsLabel}
                  totalLabel={totalLabel}
                />
              </SummaryCard>

              <SummaryCard
                label={
                  <FormattedMessage
                    id="analytics.dashboard.averageAttendance"
                    defaultMessage="Average Attendance"
                  />
                }
                value={latest.attendees_average.total}
                detail={dateRange}
                latestMonthTag={latestMonthTag}
                onDownloadData={() =>
                  goToDataDownload("enrolment_attendance_monthly")
                }
              >
                <MiniBarChart
                  data={attendeesBarData}
                  boysLabel={boysLabel}
                  girlsLabel={girlsLabel}
                  totalLabel={totalLabel}
                />
              </SummaryCard>

              <SummaryCard
                label={
                  <FormattedMessage
                    id="analytics.dashboard.beneficiaries"
                    defaultMessage="Beneficiaries"
                  />
                }
                value={latest.beneficiaries.total}
                detail={dateRange}
                latestMonthTag={latestMonthTag}
                onDownloadData={() =>
                  goToDataDownload("enrolment_attendance_monthly")
                }
              >
                <MiniBarChart
                  data={beneficiariesBarData}
                  boysLabel={boysLabel}
                  girlsLabel={girlsLabel}
                  totalLabel={totalLabel}
                />
              </SummaryCard>

              <SummaryCard
                label={
                  <FormattedMessage
                    id="analytics.dashboard.beneficiariesAverage"
                    defaultMessage="Average beneficiaries"
                  />
                }
                value={latest.beneficiaries_average?.total || 0}
                detail={dateRange}
                latestMonthTag={latestMonthTag}
                onDownloadData={() =>
                  goToDataDownload("enrolment_attendance_monthly")
                }
              >
                <MiniBarChart
                  data={beneficiariesAverageBarData}
                  boysLabel={boysLabel}
                  girlsLabel={girlsLabel}
                  totalLabel={totalLabel}
                />
              </SummaryCard>

              <SummaryCard
                label={
                  <FormattedMessage
                    id="analytics.dashboard.canteenAttendanceRate"
                    defaultMessage="Canteen attendance rate"
                  />
                }
                value={`${latest.canteen_attendance_rate || 0}%`}
                detail={dateRange}
                latestMonthTag={latestMonthTag}
                onDownloadData={() =>
                  goToDataDownload("enrolment_attendance_monthly")
                }
              >
                <MiniLineChart data={canteenAttendanceRateLineData} />
              </SummaryCard>
            </div>
          </section>

          <section className="dashboard__section">
            <h3 className="dashboard__section-title">
              <FormattedMessage
                id="analytics.dashboard.section.commodities"
                defaultMessage="Commodities"
              />
            </h3>
            <div className="dashboard__grid">
              <SummaryCard
                label={
                  <FormattedMessage
                    id="analytics.dashboard.FoodDeliveredKg"
                    defaultMessage="Food Delivered (Kg)"
                  />
                }
                value={`${(
                  latest.kg_delivered_total || 0
                ).toLocaleString()} Kg`}
                detail={dateRange}
                latestMonthTag={latestMonthTag}
                onDownloadData={() =>
                  goToDataDownload("deliveries,stock_movement_monthly")
                }
              >
                <MiniBarChart
                  data={deliveredKgBarData}
                  totalLabel={kgDeliveredLabel}
                  series={[
                    {
                      dataKey: "value",
                      label: kgDeliveredLabel,
                      color: "#2f7ed8",
                    },
                  ]}
                  valueSuffix=" Kg"
                  compactTooltip
                />
              </SummaryCard>

              <SummaryCard
                label={
                  <FormattedMessage
                    id="analytics.dashboard.FoodPurchasedKg"
                    defaultMessage="Food Purchased (Kg)"
                  />
                }
                value={`${(
                  latest.kg_purchased_total || 0
                ).toLocaleString()} Kg`}
                detail={dateRange}
                latestMonthTag={latestMonthTag}
                onDownloadData={() =>
                  goToDataDownload("purchases,stock_movement_monthly")
                }
              >
                <MiniBarChart
                  data={purchasedKgBarData}
                  totalLabel={kgPurchasedLabel}
                  series={[
                    {
                      dataKey: "value",
                      label: kgPurchasedLabel,
                      color: "#2f7ed8",
                    },
                  ]}
                  valueSuffix=" Kg"
                  compactTooltip
                />
              </SummaryCard>

              <SummaryCard
                label={
                  <FormattedMessage
                    id="analytics.dashboard.totalMoneySpent"
                    defaultMessage="Total Money Spent"
                  />
                }
                value={`${(
                  latest.expenses?.total || 0
                ).toLocaleString()} ${purchasePriceCurrencyCode}`}
                detail={dateRange}
                latestMonthTag={latestMonthTag}
                onDownloadData={() => goToDataDownload("purchases")}
              >
                <MiniBarChart
                  data={expensesBarData}
                  totalLabel={totalMoneySpentLabel}
                  series={[
                    {
                      dataKey: "food_cost",
                      label: foodCostLabel,
                      color: "#2f7ed8",
                    },
                    {
                      dataKey: "other_cost",
                      label: otherCostsLabel,
                      color: "#ff9800",
                    },
                  ]}
                  valueSuffix={` ${purchasePriceCurrencyCode}`}
                />
              </SummaryCard>

              <div className="dashboard__grid-span-2">
                <SummaryCard
                  label={
                    <FormattedMessage
                      id="analytics.dashboard.purchasePricesVariance"
                      defaultMessage="Purchase prices variance"
                    />
                  }
                  value={commodityPriceVarianceData.length}
                  hideValue
                  detail={dateRange}
                  latestMonthTag={latestMonthTag}
                  onDownloadData={() => goToDataDownload("purchases")}
                >
                  <CommodityPriceVarianceChart
                    data={commodityPriceVarianceData}
                    priceLabel={pricePaidLabel}
                    averageLabel={averageCostLabel}
                    useSchoolPurchaseDetails={isSingleSchoolSelected}
                    purchasePriceCurrencyCode={purchasePriceCurrencyCode}
                  />
                  {!isSingleSchoolSelected ? (
                    <p className="dashboard__chart-helper-text">
                      <FormattedMessage
                        id="analytics.dashboard.filterBySingleSchool"
                        defaultMessage="Filter by single school to see more detailed data"
                      />
                    </p>
                  ) : null}
                </SummaryCard>
              </div>
            </div>
          </section>

          <section className="dashboard__section">
            <h3 className="dashboard__section-title">
              <FormattedMessage
                id="analytics.dashboard.section.mealConsumption"
                defaultMessage="Meal Consumption"
              />
            </h3>
            <div className="dashboard__grid">
              <SummaryCard
                label={
                  <FormattedMessage
                    id="analytics.dashboard.kgConsumedTotal"
                    defaultMessage="Total In-kind + purchased commodities consumed"
                  />
                }
                value={`${(latest.kg_consumed_total || 0).toLocaleString()} Kg`}
                detail={dateRange}
                latestMonthTag={latestMonthTag}
                onDownloadData={() =>
                  goToDataDownload("consumptions,stock_movement_monthly")
                }
              >
                <MiniBarChart
                  data={consumedKgBarData}
                  totalLabel={kgConsumedLabel}
                  series={[
                    {
                      dataKey: "value",
                      label: kgConsumedLabel,
                      color: "#3a8f3b",
                    },
                  ]}
                  valueSuffix=" Kg"
                  compactTooltip
                />
              </SummaryCard>

              <SummaryCard
                label={
                  <FormattedMessage
                    id="analytics.dashboard.kgConsumedInKind"
                    defaultMessage="Total in-kind commodities consumed"
                  />
                }
                value={`${(
                  latest.kg_consumed_total_wfp || 0
                ).toLocaleString()} Kg`}
                detail={dateRange}
                latestMonthTag={latestMonthTag}
                onDownloadData={() =>
                  goToDataDownload("consumptions,stock_movement_monthly")
                }
              >
                <MiniBarChart
                  data={consumedWfpKgBarData}
                  totalLabel={kgConsumedWfpLabel}
                  series={[
                    {
                      dataKey: "value",
                      label: kgConsumedWfpLabel,
                      color: "#3a8f3b",
                    },
                  ]}
                  valueSuffix=" Kg"
                  compactTooltip
                />
              </SummaryCard>

              <SummaryCard
                label={
                  <FormattedMessage
                    id="analytics.dashboard.kgConsumedPurchased"
                    defaultMessage="Total purchased commodities consumed"
                  />
                }
                value={`${(
                  latest.kg_consumed_total_non_wfp || 0
                ).toLocaleString()} Kg`}
                detail={dateRange}
                latestMonthTag={latestMonthTag}
                onDownloadData={() =>
                  goToDataDownload("consumptions,stock_movement_monthly")
                }
              >
                <MiniBarChart
                  data={consumedNonWfpKgBarData}
                  totalLabel={kgConsumedNonWfpLabel}
                  series={[
                    {
                      dataKey: "value",
                      label: kgConsumedNonWfpLabel,
                      color: "#3a8f3b",
                    },
                  ]}
                  valueSuffix=" Kg"
                  compactTooltip
                />
              </SummaryCard>

              <SummaryCard
                label={
                  <FormattedMessage
                    id="analytics.dashboard.mealsServed"
                    defaultMessage="Meals served"
                  />
                }
                value={latest.meals_served || 0}
                detail={dateRange}
                latestMonthTag={latestMonthTag}
                onDownloadData={() =>
                  goToDataDownload("enrolment_attendance_monthly")
                }
              >
                <MiniBarChart
                  data={mealsServedBarData}
                  totalLabel={mealsServedLabel}
                  series={[
                    {
                      dataKey: "value",
                      label: mealsServedLabel,
                      color: "#3a8f3b",
                    },
                  ]}
                  compactTooltip
                />
              </SummaryCard>

              <SummaryCard
                label={
                  <FormattedMessage
                    id="analytics.dashboard.contributionsServed"
                    defaultMessage="Contributions Kg served"
                  />
                }
                value={`${(
                  latest.contributions_served || 0
                ).toLocaleString()} Kg`}
                detail={dateRange}
                latestMonthTag={latestMonthTag}
                onDownloadData={() =>
                  goToDataDownload("stock_movement_monthly")
                }
              >
                <MiniBarChart
                  data={contributionsServedBarData}
                  totalLabel={contributionsServedLabel}
                  series={[
                    {
                      dataKey: "value",
                      label: contributionsServedLabel,
                      color: "#3a8f3b",
                    },
                  ]}
                  compactTooltip
                  valueSuffix=" Kg"
                />
              </SummaryCard>

              <SummaryCard
                label={
                  <FormattedMessage
                    id="analytics.dashboard.averageRationPerStudent"
                    defaultMessage="Average ration per student"
                  />
                }
                value={`${latest.average_ration_per_student || 0} Kg`}
                detail={dateRange}
                latestMonthTag={latestMonthTag}
                onDownloadData={() =>
                  goToDataDownload(
                    "enrolment_attendance_monthly,stock_movement_monthly"
                  )
                }
              >
                <MiniLineChart data={averageRationLineData} unit=" Kg" />
              </SummaryCard>

              <SummaryCard
                label={
                  <FormattedMessage
                    id="analytics.dashboard.schoolCanteenFunctioningAvg"
                    defaultMessage="% School Canteen functioning average"
                  />
                }
                value={`${latest.feeding_days_rate}%`}
                detail={dateRange}
                latestMonthTag={latestMonthTag}
                onDownloadData={() =>
                  goToDataDownload("enrolment_attendance_monthly")
                }
              >
                <MiniLineChart
                  data={feedingDaysLineData}
                  extraValueLabel={feedingDaysPerSchoolAverageLabel}
                />
              </SummaryCard>

              <SummaryCard
                label={
                  <FormattedMessage
                    id="analytics.dashboard.filledSchoolDaysRate"
                    defaultMessage="% filled school days"
                  />
                }
                value={`${latest.filled_school_days_rate || 0}%`}
                detail={dateRange}
                latestMonthTag={latestMonthTag}
                onDownloadData={() => goToDataDownload("daily_entries")}
              >
                <MiniLineChart data={filledSchoolDaysLineData} />
              </SummaryCard>
            </div>
          </section>

          <section className="dashboard__section">
            <h3 className="dashboard__section-title">
              <FormattedMessage
                id="analytics.dashboard.section.reporting"
                defaultMessage="Reporting"
              />
            </h3>
            <div className="dashboard__grid">
              <SummaryCard
                label={
                  <FormattedMessage
                    id="analytics.dashboard.approvedReportsPerSchool"
                    defaultMessage="% approved reports per school"
                  />
                }
                value={`${latest.approved_reports_rate || 0}%`}
                detail={dateRange}
                latestMonthTag={latestMonthTag}
                onDownloadData={() => goToDataDownload("approved_reports")}
              >
                <MiniLineChart data={approvedReportsBarData} />
              </SummaryCard>

              <SummaryCard
                label={
                  <FormattedMessage
                    id="analytics.dashboard.activeSchools"
                    defaultMessage="Number of Active Schools with school year data"
                  />
                }
                value={latest.active_schools}
                detail={dateRange}
                latestMonthTag={latestMonthTag}
              >
                <MiniBarChart
                  data={activeSchoolsBarData}
                  totalLabel={activeSchoolsLabel}
                  series={[
                    {
                      dataKey: "value",
                      label: activeSchoolsLabel,
                      color: "#2f7ed8",
                    },
                  ]}
                  valueSuffix=" schools"
                  compactTooltip
                />
              </SummaryCard>
            </div>
          </section>
        </div>
      )}
    </Wrapper>
  );
};

export default Dashboard;
